/**
 * Maps Stripe error codes to user-friendly error messages
 */

export interface PaymentError {
  code?: string;
  type?: string;
  message: string;
}

export const getPaymentErrorMessage = (
  error: PaymentError | string
): string => {
  // If error is a string, try to extract code from it
  if (typeof error === "string") {
    return getErrorMessageFromString(error);
  }

  // Extract error code and type
  const code = error.code || "";
  const type = error.type || "";
  const message = error.message || "";

  // Map common Stripe error codes to user-friendly messages
  const errorCodeMap: Record<string, string> = {
    // Card errors
    card_declined:
      "Your card was declined. Please contact your bank or try again with a different card.",
    expired_card: "Your card has expired. Please use a different card.",
    incorrect_cvc:
      "Your card's security code (CVC) is incorrect. Please check and try again.",
    incorrect_number:
      "Your card number is incorrect. Please check and try again.",
    insufficient_funds:
      "Your card has insufficient funds. Please contact your bank or try again with a different card.",
    invalid_cvc:
      "Your card's security code (CVC) is invalid. Please check and try again.",
    invalid_expiry_month:
      "Your card's expiration month is invalid. Please check and try again.",
    invalid_expiry_year:
      "Your card's expiration year is invalid. Please check and try again.",
    invalid_number: "Your card number is invalid. Please check and try again.",

    // Processing errors
    processing_error:
      "An error occurred while processing your card. Please try again or contact your bank.",
    generic_decline:
      "Your card was declined. Please contact your bank or try again with a different card.",
    lost_card:
      "Your card was declined. Please contact your bank or try again with a different card.",
    stolen_card:
      "Your card was declined. Please contact your bank or try again with a different card.",

    // Authentication errors
    authentication_required:
      "Your card requires authentication. Please complete the authentication process and try again.",

    // Rate limit errors
    rate_limit: "Too many requests. Please wait a moment and try again.",

    // API errors
    api_connection_error:
      "Unable to connect to our payment processor. Please check your internet connection and try again.",
    api_error:
      "An error occurred with our payment processor. Please try again in a few moments.",

    // Invalid request errors
    invalid_request_error:
      "There was an error with your payment request. Please check your information and try again.",

    // Idempotency errors
    idempotency_key_in_use:
      "This payment is already being processed. Please wait a moment.",

    // Account errors
    account_closed:
      "Your payment method is not available. Please contact your bank or try again with a different card.",
    account_country_invalid_address:
      "Your billing address doesn't match your card's country. Please check and try again.",
    account_invalid:
      "Your payment method is invalid. Please contact your bank or try again with a different card.",

    // Balance errors
    balance_insufficient:
      "Your account has insufficient funds. Please contact your bank or try again with a different card.",

    // Bank errors
    bank_account_declined:
      "Your bank account was declined. Please contact your bank or try again.",
    bank_account_exists:
      "This bank account is already in use. Please contact support.",

    // Cardholder errors
    cardholder_insufficient_funds:
      "Your card has insufficient funds. Please contact your bank or try again with a different card.",

    // Charge errors
    charge_already_captured: "This payment has already been processed.",
    charge_already_refunded: "This payment has already been refunded.",
    charge_disputed: "This payment is being disputed. Please contact support.",
    charge_exceeds_source_limit:
      "The payment amount exceeds your card's limit. Please contact your bank or try again with a different card.",
    charge_expired_for_capture: "This payment has expired. Please try again.",

    // Country errors
    country_unsupported:
      "Your country is not supported. Please contact support for assistance.",

    // Currency errors
    currency_not_supported:
      "This currency is not supported. Please contact support.",

    // Customer errors
    customer_max_payment_methods:
      "You have reached the maximum number of payment methods. Please remove one and try again.",

    // Payment intent errors
    payment_intent_authentication_failure:
      "Payment authentication failed. Please try again.",
    payment_intent_incompatible_payment_method:
      "This payment method is not compatible. Please contact your bank or try again with a different card.",
    payment_intent_invalid_parameter:
      "There was an error with your payment. Please try again.",
    payment_intent_mandate_invalid:
      "Your payment authorization is invalid. Please try again.",
    payment_intent_payment_attempt_failed:
      "Your payment attempt failed. Please try again or contact your bank.",
    payment_intent_unexpected_state:
      "An unexpected error occurred. Please try again.",

    // Setup intent errors
    setup_intent_authentication_failure:
      "Payment setup authentication failed. Please try again.",

    // Source errors
    source_already_attached: "This payment method is already in use.",
    source_charge_exceeds_permit:
      "The payment amount exceeds the permitted limit. Please contact your bank or try again with a different card.",
    source_mandate_not_acceptable:
      "Your payment authorization is not acceptable. Please try again.",

    // Tax errors
    tax_id_invalid: "Your tax ID is invalid. Please check and try again.",

    // Terminal errors
    terminal_location_country_unsupported:
      "Your location is not supported. Please contact support.",

    // TLS errors
    tls_version_unsupported:
      "Your connection is not secure. Please update your browser and try again.",

    // Rate limit errors
    too_many_requests: "Too many requests. Please wait a moment and try again.",

    // Unknown errors
    unknown:
      "An unexpected error occurred. Please try again or contact support if the problem persists.",
  };

  // Check for specific error code
  if (code && errorCodeMap[code]) {
    return errorCodeMap[code];
  }

  // Check for error type
  if (type && errorCodeMap[type]) {
    return errorCodeMap[type];
  }

  // Try to extract code from message
  if (message) {
    const extractedMessage = getErrorMessageFromString(message);
    if (extractedMessage !== message) {
      return extractedMessage;
    }
  }

  // Default fallback messages based on common patterns
  if (message.toLowerCase().includes("declined")) {
    return "Your card was declined. Please contact your bank or try again with a different card.";
  }

  if (message.toLowerCase().includes("expired")) {
    return "Your card has expired. Please use a different card.";
  }

  if (message.toLowerCase().includes("insufficient")) {
    return "Your card has insufficient funds. Please contact your bank or try again with a different card.";
  }

  if (
    message.toLowerCase().includes("incorrect") ||
    message.toLowerCase().includes("invalid")
  ) {
    return "Your card information is incorrect. Please check and try again.";
  }

  if (
    message.toLowerCase().includes("cvc") ||
    message.toLowerCase().includes("cvv")
  ) {
    return "Your card's security code (CVC) is incorrect. Please check and try again.";
  }

  // Return original message if no mapping found
  return (
    message ||
    "Payment failed. Please try again or contact support if the problem persists."
  );
};

/**
 * Attempts to extract error code from error string and return user-friendly message
 */
const getErrorMessageFromString = (errorString: string): string => {
  // Try to find error code patterns in the string
  const codePatterns = [
    /code:\s*['"]([^'"]+)['"]/i,
    /error\.code:\s*['"]([^'"]+)['"]/i,
    /"code":\s*"([^"]+)"/i,
    /code:\s*(\w+)/i,
  ];

  for (const pattern of codePatterns) {
    const match = errorString.match(pattern);
    if (match && match[1]) {
      const code = match[1].toLowerCase();
      const errorCodeMap: Record<string, string> = {
        card_declined:
          "Your card was declined. Please contact your bank or try again with a different card.",
        expired_card: "Your card has expired. Please use a different card.",
        incorrect_cvc:
          "Your card's security code (CVC) is incorrect. Please check and try again.",
        insufficient_funds:
          "Your card has insufficient funds. Please contact your bank or try again with a different card.",
        processing_error:
          "An error occurred while processing your card. Please try again or contact your bank.",
      };

      if (errorCodeMap[code]) {
        return errorCodeMap[code];
      }
    }
  }

  // Return original string if no code found
  return errorString;
};
